/**
 * Spend-aggregate projections (WS-5, UID-64 "Spend / history view"). These are the
 * SHARED wire shapes the BFF's `GET /api/orders` returns (alongside the OrderSummary
 * `items` list, whose type lives in ./order) and the SPA renders — the read-side
 * counterpart to the {@link import('./order').Order} aggregate, living here beside the
 * order domain types so the contract (openapi.dist.yaml) and both apps project the SAME
 * shapes (ADR-0025).
 *
 * Spend is authoritative SERVER-SIDE: an Order carries no stored total, so every amount
 * here is DERIVED by the rule `Σ(line.price.amount × line.quantity.value) +
 * Σ(charge.amount)` (ADR-0029) at read time — a client never asserts a spend figure.
 * The derivation itself lives in the BFF `order` module (spend.ts); this file holds
 * only the framework-agnostic shapes + the pipeline-bucket vocabulary.
 */

import type { OrderStatus } from './order-status'

/**
 * The four pipeline buckets the spend view renders as stat cards (screens.md §16,
 * ADR-0033). This is a DISPLAY grouping of {@link OrderStatus}, not a new status:
 *   - `in_production` ← `in_production`
 *   - `in_transit`    ← `partially_shipped` + `shipped`
 *   - `on_hold`       ← `on_hold`
 *   - `delivered`     ← `delivered`
 * Statuses outside these (draft/placed/closed/cancelled) contribute to the top-line
 * spend total but are not a pipeline bucket.
 */
export const SPEND_BUCKETS = ['in_production', 'in_transit', 'on_hold', 'delivered'] as const

export type SpendBucket = (typeof SPEND_BUCKETS)[number]

/**
 * The ONE canonical {@link OrderStatus} → {@link SpendBucket} map (UID-404, ADR-0126 D3).
 * Consumed by BOTH the api pipeline aggregation (`order/spend.ts` `buildPipeline`) and the
 * web toolbar filter (`orders/domain.ts` `ORDER_STATUS_TO_BUCKET` / `matchesToolbarFilter`),
 * so a pipeline tile count and the table rows that tile filters to can never disagree.
 *
 * WHY IT LIVES HERE: until UID-404 there were TWO copies — a `switch` in `spend.ts` with a
 * `default: return null` arm, and an object literal in web `domain.ts` — and they had
 * silently DRIFTED. The api copy dropped `placed` AND `closed`, so every tenant holding an
 * order in either state saw a pipeline tile count short by that order, independent of any
 * date window. The `default` arm is precisely the mechanism by which two enum members went
 * missing unnoticed, so the replacement is a TOTAL, exhaustively-typed record — adding a
 * new `OrderStatus` now forces an explicit bucket decision at compile time. The web copy
 * was authoritative on evidence: its taxonomy was re-verified first-hand against the
 * prototype under UID-95 O5 and was already pinned by unit tests.
 *
 * The taxonomy (UX spec §1.6):
 *   - `in_production` ← `placed` + `in_production`  (§1.6.1 — `placed` collapses in; the
 *     prototype stamps a freshly-placed order `in_production` at the data-model level)
 *   - `in_transit`    ← `shipped` + `partially_shipped`  (§1.6.2)
 *   - `on_hold`       ← `on_hold`
 *   - `delivered`     ← `delivered` + `closed`  (a closed order is a delivered one whose
 *     paperwork is settled)
 *
 * `draft` and `cancelled` map to `undefined` DELIBERATELY (§1.6 item 4, ADR-0126 D4): a
 * draft is not placed yet and a cancelled order is out of the active pipeline, but both
 * remain visible in the table (`cancelled` via its own filter chip, ADR-0036 §5). They
 * still contribute to the top-line spend total — they are simply not a tile. This is why
 * the card's "N in flight · M total" line is NOT expected to equal the unfiltered row
 * count; that is correct behavior, not a defect.
 */
export const ORDER_STATUS_TO_SPEND_BUCKET: Readonly<Record<OrderStatus, SpendBucket | undefined>> =
  Object.freeze({
    draft: undefined,
    placed: 'in_production',
    in_production: 'in_production',
    partially_shipped: 'in_transit',
    shipped: 'in_transit',
    delivered: 'delivered',
    closed: 'delivered',
    on_hold: 'on_hold',
    cancelled: undefined,
  } as const)

/** Per-bucket rollup: how many orders, how many units, and how much spend. */
export interface SpendPipelineBucket {
  readonly bucket: SpendBucket
  readonly count: number
  /** Σ of every line's `quantity.value` across the bucket's orders. */
  readonly units: number
  /** Derived spend for the bucket's orders, as a decimal string. */
  readonly amount: string
}

/**
 * The spend summary for a (optionally date-windowed) set of orders (screens.md §16).
 * `total`/`averageOrderValue`/`pipeline[].amount` are decimal strings; `currency` is
 * carried once at the top (a single-currency assumption for this view). `changePct`
 * is the percentage change in total spend vs the immediately-preceding equal-length
 * period, or `null` when it is undefined (no window requested, or a zero prior-period
 * total — division by zero).
 */
export interface SpendSummary {
  readonly total: string
  readonly orderCount: number
  readonly averageOrderValue: string
  readonly currency: string
  readonly changePct: number | null
  readonly pipeline: readonly SpendPipelineBucket[]
}
